/**
 * @packageDocumentation SimpleTooltip
 *
 * Wrap a component adding a text tooltip on it.
 *
 * @category Component
 */

import clsx from 'clsx';
import isString from 'lodash/isString';
import React, { PropsWithChildren, ReactNode } from 'react';
import { Focusable, TooltipTriggerProps } from 'react-aria';
import { Button, ButtonProps, OverlayArrow, Tooltip, TooltipProps, TooltipTrigger } from 'react-aria-components';
import useDataTestId from '~/hooks/useDataTestId';
import styles from './SimpleTooltip.module.scss';
import Text from '../typographies/Text';

/**
 * The props type for {@link SimpleTooltip}.
 *
 * @category Props
 *
 */
export interface SimpleTooltipProps {
  allowTooltip?: boolean;
  title: ReactNode;
  offset?: TooltipProps['offset'];
  crossOffset?: TooltipProps['crossOffset'];
  placement?: TooltipProps['placement'];
  delay?: TooltipTriggerProps['delay'];
  closeDelay?: TooltipTriggerProps['closeDelay'];
  open?: TooltipTriggerProps['isOpen'];
  onPress?: ButtonProps['onPress'];
  className?: string;
  tooltipClassName?: string;
  dataTestId?: string;
  mode?: 'button' | 'div' | 'span';
  theme?: 'light' | 'dark';
  arrow?: boolean;
}

/**
 * Wrap a component adding a text tooltip on it.
 *
 * ```tsx
 * import SimpleTooltip from '~/components/data-display/SimpleTooltip';
 *
 * ...
 *
 * <SimpleTooltip placement="bottom" title={t('table.linked_counterparts_modal.tooltip')}>
 *   <Icon component={IconLinkOn} size="xs" />
 * </SimpleTooltip>
 * ```
 *
 * @param props - The props defined.
 *
 * @category Component
 */
const SimpleTooltip: React.FC<PropsWithChildren<SimpleTooltipProps>> = ({
  children,
  title,
  allowTooltip = true,
  placement,
  delay = 750,
  closeDelay = 500,
  open,
  className,
  dataTestId,
  offset,
  crossOffset,
  onPress,
  tooltipClassName,
  mode = 'button',
  theme = 'dark',
  arrow,
}) => {
  const dt = useDataTestId(dataTestId);

  if (mode !== 'button' && (!title || !allowTooltip)) {
    const WrapperComponent = mode === 'div' ? 'div' : 'span';
    return (
      <WrapperComponent data-testid={dt('triggerButton')} className={clsx(styles.customTrigger, className)}>
        {children}
      </WrapperComponent>
    );
  }

  return (
    <TooltipTrigger isDisabled={!allowTooltip} delay={delay} closeDelay={closeDelay} isOpen={open}>
      {mode === 'button' ? (
        <Button data-testid={dt('triggerButton')} className={clsx(styles.trigger, className)} onPress={onPress}>
          {children}
        </Button>
      ) : (
        <CustomTrigger dataTestId={dt('triggerButton')} mode={mode} className={className}>
          {children}
        </CustomTrigger>
      )}
      {title && (
        <Tooltip
          data-testid={dt('tooltip')}
          placement={placement}
          offset={offset}
          crossOffset={crossOffset}
          className={clsx(styles.tooltip, { [styles.light!]: theme === 'light' }, tooltipClassName)}
        >
          {arrow && (
            <OverlayArrow className={styles.arrow}>
              <svg width={8} height={8} viewBox="0 0 8 8">
                <path d="M0 0 L4 4 L8 0" />
              </svg>
            </OverlayArrow>
          )}
          {isString(title) ? (
            <Text data-testid={dt('tooltipText')} variant="caption" weight="medium">
              {title}
            </Text>
          ) : (
            title
          )}
        </Tooltip>
      )}
    </TooltipTrigger>
  );
};

const CustomTrigger: React.FC<{
  children: ReactNode;
  mode?: 'div' | 'span';
  className?: string;
  dataTestId?: string;
}> = ({ children, mode = 'div', className, dataTestId }) => {
  const dt = useDataTestId(dataTestId);
  const WrapperComponent = mode === 'div' ? 'div' : 'span';

  return (
    <Focusable>
      <WrapperComponent role="button" data-testid={dt()} className={clsx(styles.customTrigger, className)}>
        {children}
      </WrapperComponent>
    </Focusable>
  );
};

export default SimpleTooltip;
